import { Injectable, APP_INITIALIZER, Provider, Type, Inject, Injector, Optional } from '@angular/core';
import { BootstrapperBase, ArgumentError, LogService, TOKENS as COMMON_TOKENS } from '@trustedshops/tswp-core-common';

import { IdentityService, SessionProviderService, Session, TOKENS } from '@trustedshops/tswp-core-authorization';
import { INSTANA_EARLY_INIT_COMPLETE, IDENTITY_INIT_COMPLETE } from '@trustedshops/tswp-core-common-diag-instana';

/**
 * Initializes the IdentityService with data
 */
@Injectable()
export class IdentityBootstrapper implements BootstrapperBase {
  //#region Private Fields
  private readonly TYPE: string = 'IdentityBootstrapper';
  private readonly _identityService: IdentityService;
  private readonly _injector: Injector;
  //#endregion

  //#region Ctor
  /**
   * Creates an instance of IdentityBootstrapper
   * @param identityService The IdentityService to initialize
   * @param injector The Angular injector
   * @param logService The log service for logging
   */
  public constructor(
    @Inject(TOKENS.IdentityService) identityService: IdentityService,
    @Inject(Injector) injector: Injector,
    @Inject(COMMON_TOKENS.LogService) private readonly _logService: LogService) {

    this._identityService = identityService;
    this._injector = injector;
  }
  //#endregion

  //#region Public Methods
  /**
   * Initializes the ApplicationService
   * @param environment The environment to use when initializing
   * @throws {ArgumentError} Thrown when sessionService argument is null or undefined.
   */
  public async initialize<TOptions>(sessionProvider?: Type<SessionProviderService<Session, TOptions>>, options?: TOptions): Promise<void> {
    if (!sessionProvider) {
      throw new ArgumentError('sessionProvider');
    }

    if (!options) {
      throw new ArgumentError('options');
    }

    this._logService.trace(this.TYPE, `Initializing identity with ${sessionProvider.name}`);
    const sessionProviderInstance = this._injector.get(sessionProvider);
    await this._identityService.loadIdentityFrom(sessionProviderInstance, options);
  }
  //#endregion
}

/**
 * Initializes the IdentityService with data like a session provider.
 *
 * **Execution Order:**
 * This initializer runs AFTER Instana early initialization completes and BEFORE
 * Instana late initialization. The dependency chain ensures proper sequential execution:
 *
 * 1. **Instana Early Initialization** (no dependencies)
 *    - Sets up global error handlers
 *    - Loads Instana EUM script
 *    - Provides INSTANA_EARLY_INIT_COMPLETE token
 *
 * 2. **Identity Initialization** (depends on INSTANA_EARLY_INIT_COMPLETE)
 *    - Waits for Instana early init to complete
 *    - Initializes identity/session provider
 *    - Provides IDENTITY_INIT_COMPLETE token
 *
 * 3. **Instana Late Initialization** (depends on IDENTITY_INIT_COMPLETE)
 *    - Waits for Identity init to complete
 *    - Sets up user and route tracking
 *
 * **Two-Provider Pattern:**
 * Similar to InstanaLoggingEarlyInitializer, this uses two providers:
 * - Token provider: Creates promise, allows other initializers to depend on it
 * - APP_INITIALIZER provider: Ensures execution, reuses same promise
 *
 * @param sessionProvider The session provider to use in the application.
 * @param options The initialization options for the session provider.
 */
export function IdentityInitializer<TOptions>(
  sessionProvider: Type<SessionProviderService<Session, TOptions>>,
  options: TOptions): Array<Provider> {

  return [
    IdentityBootstrapper,
    // Provider 1: Token that represents the initialization promise
    // This allows Instana late initializer to depend on it for sequential execution
    {
      provide: IDENTITY_INIT_COMPLETE,
      useFactory: (
        initializer: IdentityBootstrapper,
        instanaEarlyInit?: Promise<void>
      ): Promise<void> => {
        // Use IIFE (Immediately Invoked Function Expression) pattern:
        // (async () => { ... })() immediately invokes the async function,
        // starting promise execution right away rather than returning a function.
        // This is necessary because Angular's useFactory expects a Promise<void>,
        // not a function that returns a Promise<void>.
        return (async () => {
          // Wait for Instana early initialization to complete (if provided)
          // This ensures error handlers are active before identity initialization
          if (instanaEarlyInit) {
            await instanaEarlyInit;
          }
          return initializer.initialize(sessionProvider, options);
        })();
      },
      deps: [
        IdentityBootstrapper,
        [new Optional(), INSTANA_EARLY_INIT_COMPLETE]
      ]
    },
    // Provider 2: APP_INITIALIZER that ensures initialization runs
    // Depends on the token to reuse the same promise (avoids duplicate execution)
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: (identityInitPromise: Promise<void>) => {
        // Return an async function that awaits the promise
        // This ensures the promise completes before Angular continues bootstrapping
        return async () => {
          await identityInitPromise;
        };
      },
      deps: [IDENTITY_INIT_COMPLETE],
    }
  ];
}
